import { useEffect, useRef, useState } from 'react';

// Mirrors NumberCellInput.toDraftString — duplicated rather than imported
// so this hook stays standalone and can be reused with any underlying
// input component (FormControl, FloatInput, anything that accepts
// value/onChange/onFocus/onBlur).
const toDraft = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? String(n) : '';
};

/**
 * Hook that turns any controlled input into a draft-aware numeric input.
 *
 * Returns props you spread onto an input: { value, onChange, onFocus,
 * onBlur }. While the input is focused, the draft holds the literal
 * string the user is typing — including '' — so backspacing the last
 * digit doesn't immediately snap back to 0/the parent value (the classic
 * `+e.target.value` bug). The parent is still notified on every
 * keystroke that produces a finite number, so anything derived
 * (canvas, downstream calculations, sibling inputs) keeps reacting in
 * real time.
 *
 * Empty draft handling:
 *  - `skipEmptyCommit: true` (default for required numeric fields like
 *    spacing/tilt): parent isn't called when the field is empty, so its
 *    stored value stays as the last valid number. Blur re-syncs the
 *    draft to the parent value, so a cleared-and-tabbed-away field
 *    snaps back to the previous value rather than NaN-ing downstream.
 *  - `skipEmptyCommit: false`: parent receives `undefined` on empty.
 *    Use only when the parent can meaningfully represent "no value".
 *
 * NumberCellInput re-implements this same logic inline; we could swap
 * it over to this hook in a follow-up, but didn't to keep this change
 * surgical.
 */
export default function useNumberDraft(
  value,
  onCommit,
  { skipEmptyCommit = true } = {}
) {
  const [draft, setDraft] = useState(() => toDraft(value));
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setDraft(toDraft(value));
  }, [value]);

  return {
    value: draft,
    onChange: (e) => {
      const raw = e.target.value;
      setDraft(raw);
      if (raw === '') {
        if (!skipEmptyCommit) onCommit(undefined);
        return;
      }
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) onCommit(parsed);
    },
    onFocus: () => {
      focusedRef.current = true;
    },
    onBlur: () => {
      focusedRef.current = false;
      setDraft(toDraft(value));
    },
  };
}
